import { Link } from "react-router-dom";
import {
    ArrowRight,
    Check,
    Phone,
    MessageCircle,
    Car,
    MapPin,
    Wrench,
    ShieldCheck,
} from "lucide-react";
import { SERVICES, buildWhatsAppLink, buildTelLink } from "@/lib/constants";
import SectionTitle from "@/components/SectionTitle";

const iconMap = { Car, MapPin, Wrench };

export default function ServicePage({ slug }) {
    const service = SERVICES.find((s) => s.slug === slug);
    if (!service) return null;
    const Icon = iconMap[service.icon];

    const others = SERVICES.filter((s) => s.slug !== slug);

    return (
        <div data-testid={`service-page-${slug}`}>
            {/* Hero */}
            <section className="bg-[#1a1a1a] text-white py-20 md:py-28 relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.07]" style={{
                    backgroundImage: "linear-gradient(#fec200 1px, transparent 1px), linear-gradient(90deg, #fec200 1px, transparent 1px)",
                    backgroundSize: "60px 60px",
                }} />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-7">
                            <div className="inline-flex items-center gap-2 bg-[#fec200] text-[#1a1a1a] px-4 py-1.5 font-bold text-xs uppercase tracking-widest mb-6">
                                <Icon className="w-4 h-4" />
                                Serviço PEG
                            </div>
                            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95]">
                                {service.title}
                            </h1>
                            <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-2xl">
                                {service.long}
                            </p>
                            <div className="mt-9 flex flex-col sm:flex-row gap-4">
                                <a
                                    href={buildWhatsAppLink(service.phone.raw, service.whatsappMessage)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`${slug}-hero-whatsapp`}
                                    className="inline-flex items-center justify-center gap-2 bg-[#fec200] text-[#1a1a1a] font-bold px-8 py-4 hover:bg-white transition-colors"
                                >
                                    <MessageCircle className="w-5 h-5" />
                                    Solicitar pelo WhatsApp
                                </a>
                                <a
                                    href={buildTelLink(service.phone.raw)}
                                    data-testid={`${slug}-hero-call`}
                                    className="inline-flex items-center justify-center gap-2 border-2 border-white text-white font-bold px-8 py-4 hover:bg-white hover:text-[#1a1a1a] transition-colors"
                                >
                                    <Phone className="w-5 h-5" />
                                    {service.phone.display}
                                </a>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="relative">
                                <div className="absolute -inset-3 bg-[#fec200]" />
                                <img
                                    src={service.image}
                                    alt={service.title}
                                    className="relative w-full h-[420px] object-cover"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* App screenshot showcase (optional) */}
            {service.appScreenshot && (
                <section data-testid={`${slug}-app-section`} className="bg-[#f5f5f5] py-20 md:py-28">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid lg:grid-cols-12 gap-12 items-center">
                            <div className="lg:col-span-6 order-2 lg:order-1 flex justify-center">
                                <div className="relative">
                                    {/* phone frame */}
                                    <div className="relative bg-[#1a1a1a] rounded-[2.5rem] p-3 shadow-2xl w-[280px] sm:w-[320px]">
                                        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-5 bg-[#1a1a1a] rounded-b-2xl z-10" />
                                        <img
                                            src={service.appScreenshot}
                                            alt="Aplicativo PEG Rastreamento"
                                            className="rounded-[2rem] w-full h-auto block"
                                            data-testid={`${slug}-app-screenshot`}
                                        />
                                    </div>
                                    {/* yellow blob */}
                                    <div className="absolute -z-10 -top-6 -right-6 w-40 h-40 bg-[#fec200]" />
                                    <div className="absolute -z-10 -bottom-6 -left-6 w-32 h-32 bg-[#1a1a1a]" />
                                </div>
                            </div>
                            <div className="lg:col-span-6 order-1 lg:order-2">
                                <SectionTitle
                                    eyebrow="Aplicativo PEG"
                                    title="Controle total na palma da sua mão"
                                    subtitle="O aplicativo PEG Rastreamento traz tudo o que você precisa para acompanhar e proteger o seu veículo em uma única tela: localização, histórico, âncora, bloqueio de ignição, velocidade, abastecimento e muito mais."
                                />
                                <div className="mt-8 grid grid-cols-2 gap-3">
                                    {[
                                        "Localizar",
                                        "Histórico",
                                        "Âncora virtual",
                                        "Fronteira / cerca",
                                        "Bloqueio de ignição",
                                        "Velocidade",
                                        "Abastecimento",
                                        "Manutenção",
                                    ].map((f) => (
                                        <div
                                            key={f}
                                            className="bg-white border-l-4 border-[#fec200] px-3 py-2 text-sm font-semibold text-[#1a1a1a]"
                                            data-testid={`${slug}-app-feature-${f.toLowerCase().replace(/\s/g, "-")}`}
                                        >
                                            {f}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Instagram reel embed (optional) */}
            {service.instagramEmbed && (
                <section data-testid={`${slug}-reel-section`} className="bg-[#1a1a1a] py-20 md:py-28">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid lg:grid-cols-12 gap-12 items-center">
                            <div className="lg:col-span-6">
                                <SectionTitle
                                    eyebrow="Veja em ação"
                                    title="Como funciona o rastreamento PEG"
                                    subtitle="Assista a uma demonstração real do nosso sistema de rastreamento veicular. Tecnologia, praticidade e segurança em tempo real, direto na palma da sua mão."
                                    light
                                />
                                <a
                                    href="https://www.instagram.com/reel/CwxztAkOXTh/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`${slug}-reel-instagram-link`}
                                    className="mt-8 inline-flex items-center gap-2 bg-[#fec200] text-[#1a1a1a] font-bold px-6 py-3 hover:bg-white transition-colors"
                                >
                                    Ver no Instagram
                                </a>
                            </div>
                            <div className="lg:col-span-6 flex justify-center">
                                <div className="relative w-full max-w-[420px]">
                                    <div className="absolute -inset-2 bg-[#fec200]" />
                                    <div className="relative bg-black" style={{ paddingBottom: "125%" }}>
                                        <iframe
                                            title="PEG Rastreamento — Reel"
                                            src={service.instagramEmbed}
                                            className="absolute inset-0 w-full h-full"
                                            frameBorder="0"
                                            scrolling="no"
                                            allow="encrypted-media; autoplay; clipboard-write"
                                            allowFullScreen
                                            data-testid={`${slug}-reel-iframe`}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Video showcase (optional) */}
            {service.video && (
                <section data-testid={`${slug}-video-section`} className="bg-[#1a1a1a] py-20 md:py-28">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="max-w-3xl">
                            <SectionTitle
                                eyebrow="Veja em ação"
                                title="Conheça nossa frota"
                                subtitle="Veículos revisados, modernos e prontos para te atender com segurança e conforto."
                                light
                            />
                        </div>
                        <div className="mt-12 relative">
                            <div className="absolute -inset-2 bg-[#fec200]" />
                            <video
                                src={service.video}
                                className="relative w-full max-h-[640px] object-cover bg-black"
                                controls
                                playsInline
                                preload="metadata"
                                data-testid={`${slug}-video-player`}
                            />
                        </div>
                    </div>
                </section>
            )}

            {/* Benefits */}
            <section className="bg-[#f5f5f5] py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionTitle
                        eyebrow="Benefícios"
                        title="O que você ganha com a PEG"
                        subtitle={`Tudo que o nosso serviço de ${service.title.toLowerCase()} oferece para tornar sua vida mais simples, segura e prática.`}
                    />
                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {service.benefits.map((b, i) => (
                            <div
                                key={i}
                                data-testid={`${slug}-benefit-${i}`}
                                className="bg-white p-6 border-l-4 border-[#fec200] hover:bg-[#1a1a1a] hover:text-white group transition-colors"
                            >
                                <div className="w-10 h-10 bg-[#fec200] flex items-center justify-center mb-4">
                                    <Check className="w-5 h-5 text-[#1a1a1a]" />
                                </div>
                                <p className="font-semibold text-lg">{b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How it works / trust strip */}
            <section className="bg-white py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-6">
                        <SectionTitle
                            eyebrow="Como funciona"
                            title="Simples, rápido e sem complicação."
                        />
                        <div className="mt-8 space-y-5">
                            {[
                                "Você entra em contato pelo WhatsApp ou telefone.",
                                "Nossa equipe te orienta e apresenta a melhor opção para o seu caso.",
                                "Confirmação rápida e atendimento iniciado em tempo recorde.",
                                "Suporte humanizado durante todo o processo.",
                            ].map((step, idx) => (
                                <div
                                    key={idx}
                                    className="flex items-start gap-4"
                                >
                                    <div className="w-10 h-10 bg-[#1a1a1a] text-[#fec200] font-display font-black flex items-center justify-center flex-shrink-0">
                                        {idx + 1}
                                    </div>
                                    <p className="text-[#1a1a1a] text-lg pt-1">{step}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="lg:col-span-6">
                        <div className="bg-[#1a1a1a] text-white p-8 md:p-10">
                            <ShieldCheck className="w-12 h-12 text-[#fec200]" />
                            <h3 className="font-display text-3xl font-black mt-5">
                                Compromisso PEG
                            </h3>
                            <p className="mt-4 text-white/70 leading-relaxed">
                                Atendimento confiável, equipe especializada e tecnologia de ponta. Trabalhamos para que cada cliente saia mais tranquilo do que entrou.
                            </p>
                            <a
                                href={buildWhatsAppLink(service.phone.raw, service.whatsappMessage)}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`${slug}-trust-cta`}
                                className="mt-7 inline-flex items-center gap-2 bg-[#fec200] text-[#1a1a1a] font-bold px-6 py-3 hover:bg-white transition-colors"
                            >
                                Solicitar atendimento agora
                                <ArrowRight className="w-4 h-4" />
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Other services */}
            <section className="bg-[#f5f5f5] py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionTitle
                        eyebrow="Outros serviços"
                        title="Conheça as demais soluções da PEG"
                    />
                    <div className="mt-12 grid md:grid-cols-2 gap-6">
                        {others.map((o) => {
                            const OIcon = iconMap[o.icon];
                            return (
                                <Link
                                    key={o.slug}
                                    to={o.path}
                                    data-testid={`${slug}-other-${o.slug}`}
                                    className="group bg-white p-7 border border-transparent hover:border-[#1a1a1a] flex items-center justify-between gap-6 transition-all"
                                >
                                    <div className="flex items-center gap-5">
                                        <div className="w-14 h-14 bg-[#fec200] flex items-center justify-center flex-shrink-0">
                                            <OIcon className="w-7 h-7 text-[#1a1a1a]" />
                                        </div>
                                        <div>
                                            <h4 className="font-display text-xl font-black text-[#1a1a1a]">
                                                {o.title}
                                            </h4>
                                            <p className="text-[#4a4a4a] text-sm mt-1">
                                                {o.short}
                                            </p>
                                        </div>
                                    </div>
                                    <ArrowRight className="w-6 h-6 text-[#1a1a1a] group-hover:translate-x-1 transition-transform flex-shrink-0" />
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="bg-[#fec200] py-16">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-[#1a1a1a]">
                        Pronto para solicitar {service.title.toLowerCase()}?
                    </h2>
                    <p className="mt-4 text-[#1a1a1a]/80 text-lg">
                        Nossa equipe está disponível para te atender agora mesmo.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href={buildTelLink(service.phone.raw)}
                            data-testid={`${slug}-final-call`}
                            className="inline-flex items-center justify-center gap-2 bg-[#1a1a1a] text-white font-bold px-8 py-4 hover:bg-white hover:text-[#1a1a1a] transition-colors"
                        >
                            <Phone className="w-5 h-5" />
                            Ligar: {service.phone.display}
                        </a>
                        <a
                            href={buildWhatsAppLink(service.phone.raw, service.whatsappMessage)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`${slug}-final-whatsapp`}
                            className="inline-flex items-center justify-center gap-2 bg-white text-[#1a1a1a] font-bold px-8 py-4 hover:bg-[#1a1a1a] hover:text-white transition-colors"
                        >
                            <MessageCircle className="w-5 h-5" />
                            Falar no WhatsApp
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
